import * as XLSX from 'xlsx';

import { readFileArrayBuffer } from '@/lib/daily-reports/readFileArrayBuffer';

import {
  attachCoverageToPrices,
  missingSpasFromCoverage,
  summarizeCoverageBySpa,
} from './attachCoverage';
import { classifyEatonSheet } from './classifySheet';
import { findDuplicateCatalogs } from './findDuplicates';
import { dedupeCoverage, parseCoverageSheet } from './parseCoverage';
import { parseFlatPriceSheet } from './parseFlatPrices';
import { parseSpaPriceSheet } from './parseSpaPrices';
import type {
  EatonCoverageRow,
  EatonDspFileResult,
  EatonPriceRow,
} from './types';

function sheetRows(workbook: XLSX.WorkBook, name: string): unknown[][] {
  const sheet = workbook.Sheets[name];
  if (!sheet) return [];
  return XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
  });
}

function parseSheet(
  kind: ReturnType<typeof classifyEatonSheet>,
  rows: unknown[][],
  fileName: string,
  sheetName: string,
): { prices: EatonPriceRow[]; coverage: EatonCoverageRow[] } {
  if (kind === 'coverage') {
    return { prices: [], coverage: parseCoverageSheet(rows, sheetName) };
  }
  if (kind === 'flatPrices') {
    return {
      prices: parseFlatPriceSheet(rows, fileName, sheetName),
      coverage: [],
    };
  }
  if (kind === 'spaPrices') {
    return {
      prices: parseSpaPriceSheet(rows, fileName, sheetName),
      coverage: [],
    };
  }
  return { prices: [], coverage: [] };
}

/**
 * Flatten every SPA / already-flat tab and join Westburne customer coverage
 * onto those prices. Does not explode one price per customer.
 */
export function extractEatonDspWorkbook(
  workbook: XLSX.WorkBook,
  fileName: string,
): EatonDspFileResult {
  const prices: EatonPriceRow[] = [];
  const coverage: EatonCoverageRow[] = [];
  const skippedSheets: string[] = [];

  for (const sheetName of workbook.SheetNames) {
    const rows = sheetRows(workbook, sheetName);
    const kind = classifyEatonSheet(rows);
    const parsed = parseSheet(kind, rows, fileName, sheetName);
    prices.push(...parsed.prices);
    coverage.push(...parsed.coverage);
    if (kind === 'ignored') skippedSheets.push(sheetName);
  }

  if (prices.length === 0 && coverage.length === 0) {
    return { status: 'unrecognized', fileName };
  }

  const uniqueCoverage = dedupeCoverage(coverage);
  const coverageBySpa = summarizeCoverageBySpa(uniqueCoverage);
  const stamped = attachCoverageToPrices(prices, coverageBySpa);
  return {
    status: 'extracted',
    fileName,
    prices: stamped,
    coverage: uniqueCoverage,
    duplicates: findDuplicateCatalogs(stamped),
    missingSpas: missingSpasFromCoverage(coverageBySpa, stamped),
    skippedSheets,
  };
}

export async function extractEatonDspFile(
  file: File,
): Promise<EatonDspFileResult> {
  try {
    const workbook = XLSX.read(await readFileArrayBuffer(file), {
      type: 'array',
    });
    return extractEatonDspWorkbook(workbook, file.name);
  } catch {
    return { status: 'unreadable', fileName: file.name };
  }
}
